/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { css, cssMap, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import { TOOLBAR_ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { ToolbarButton } from '@atlaskit/editor-common/ui-menu';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { WidthPlugin } from '@atlaskit/editor-plugin-width';
import AkSuggestedEditIcon from '@atlaskit/icon-lab/core/suggested-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Loading from '@atlassian/generative-ai-modal/assets/Loading';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import SuggestedEditIcon from '../../assets/SuggestedEditIcon';
import { useEditorToolbarChangeDetector } from '../../hooks/useEditorToolbarChangeDetector';

const styles = cssMap({
	unreadBadgeCorner: {
		position: 'absolute',
		right: token('space.negative.050'),
		top: token('space.negative.025'),
	},

	buttonWrapper: {
		position: 'relative',
	},

	truncatingText: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: 'inline-block',
	},
});

/**
 * Copied from generative-ai-modal
 */

const isFirefox: boolean =
	typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const rotationAnimation = keyframes({
	from: {
		'--gradient-angle': '0deg',
	},
	to: {
		'--gradient-angle': '360deg',
	},
});

const prismBorderStyles = css({
	'@property --gradient-angle': {
		syntax: "'<angle>'",
		initialValue: '270deg',
		inherits: 'false',
	},
	position: 'relative',
	borderRadius: token('radius.large', '6px'),
	boxShadow: 'none',
	'&::before, &::after': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		width: 'calc(100% + 2px)',
		height: 'calc(100% + 2px)',
		top: '-1px',
		left: '-1px',
		borderRadius: `calc(${token('radius.large', '6px')} + 1px)`,
		transform: 'translate3d(0, 0, 0)',
	},
	'&::after': {
		filter: 'blur(8px)',
		opacity: 0.5,
	},
	'@media (prefers-reduced-motion)': {
		animation: 'none',
	},
});

const PRISM_BORDER_STEP_1 = '#0065FF';
const PRISM_BORDER_STEP_2 = '#0469FF';
const PRISM_BORDER_STEP_3 = '#BF63F3';
const PRISM_BORDER_STEP_4 = '#FFA900';

const staticStyles = cssMap({
	default: {
		'&::before, &::after': {
			background: `conic-gradient(
				from var(--gradient-angle, 270deg),
				${PRISM_BORDER_STEP_1} 0%,
				${PRISM_BORDER_STEP_2} 20%,
				${PRISM_BORDER_STEP_3} 50%,
				${PRISM_BORDER_STEP_4} 56%,
				${PRISM_BORDER_STEP_1} 100%
			)`,
		},
	},
	firefox: {
		'&::before, &::after': {
			backgroundSize: '200%',
			background: `linear-gradient(90deg,
			${PRISM_BORDER_STEP_1} 0%,
			${PRISM_BORDER_STEP_2} 12%,
			${PRISM_BORDER_STEP_3} 24%,
			${PRISM_BORDER_STEP_4} 48%,
			${PRISM_BORDER_STEP_3} 64%,
			${PRISM_BORDER_STEP_2} 80%,
			${PRISM_BORDER_STEP_1} 100%
			)`,
		},
	},
});

const animationStyles = cssMap({
	default: {
		'&::before, &::after': {
			animationName: rotationAnimation,
			animationDuration: '2s',
			animationTimingFunction: 'linear',
			animationIterationCount: 'infinite',
		},
	},
	firefox: {
		'&::before, &::after': {
			animationName: rotationAnimation,
			animationDuration: '1s',
			animationTimingFunction: 'linear',
			animationIterationCount: 'infinite',
			animationDirection: 'normal',
		},
	},
});

/**
 * Copied from editor-common - compiled styles should be local not imported
 */
const wrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div, > span': {
		display: 'flex',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div > div': {
		display: 'flex',
	},
	marginLeft: 0,
	minWidth: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not([disabled])::after': {
			border: 'none',
		},
	},
});

const svgIconStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	button: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		svg: {
			pointerEvents: 'none',
		},
	},
});

type Props = {
	api?: PublicPluginAPI<[WidthPlugin]> | undefined;
	ButtonWrapper?: React.ComponentType<{ children: React.ReactNode }>;
	disabled: boolean;
	loading?: boolean;
	onClick: () => void;
	proactive?: boolean;
	selected?: boolean;
	unreadCount?: number;
};

// This const is derived from the breakpoint where the toolbar hides its icons. It is used to hide the text in the AI button.
// Derived from values from platform/packages/editor/editor-core/src/ui/Appearance/FullPage/MainToolbar.tsx
const AI_BUTTON_WIDTH_BREAKPOINT = 1076;

const fadeOut = keyframes({
	/**
	 * 0% keyframe is omitted as the blur (&::after) is at 0.5 while the
	 * border (&::before) is at 1
	 */
	'100%': {
		opacity: 0,
	},
});

const fadeOutAnimation = css({
	'&::before, &::after': {
		animationName: fadeOut,
		animationDuration: '160ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'forwards',
		animationDelay: '3000ms',
	},
});

const backgroundStyles = css({
	/**
	 * Override the border radius as the button radius is smaller than the styles from
	 * FloatingContainer
	 */
	'&::before, &::after': {
		borderRadius: `calc(${token('radius.small')} + 1px)`,
	},
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.small'),
});

const LoadingContainer = ({
	isLoading,
	children,
}: {
	children: React.ReactNode;
	isLoading?: boolean;
}) => {
	const [isPersisting, setIsPersisting] = useState(isLoading ?? false);

	useEffect(() => {
		if (isLoading) {
			setIsPersisting(true);
		} else {
			const timeoutId = setTimeout(() => setIsPersisting(false), 3600);
			return () => clearTimeout(timeoutId);
		}
	}, [isLoading]);

	if (isLoading || isPersisting) {
		return (
			<div
				css={[
					prismBorderStyles,
					staticStyles[isFirefox ? 'firefox' : 'default'],
					backgroundStyles,
					isLoading && animationStyles[isFirefox ? 'firefox' : 'default'],
					!isLoading && isPersisting && fadeOutAnimation,
				]}
			>
				{children}
			</div>
		);
	}
	return <Box xcss={styles.buttonWrapper}>{children}</Box>;
};

const ProactiveAIButton = ({
	onClick,
	isDisabled,
	isSelected,
	isLoading,
	unreadCount,
	api,
}: {
	api?: PublicPluginAPI<[WidthPlugin]> | undefined;
	isDisabled: boolean;
	isLoading?: boolean;
	isSelected?: boolean;
	onClick: () => void;
	unreadCount?: number;
}) => {
	const { formatMessage } = useIntl();

	const width = useSharedPluginStateSelector(api, 'width.width');

	const shouldShowLabel = width ? width >= AI_BUTTON_WIDTH_BREAKPOINT : true;

	const title = isSelected
		? formatMessage(sharedMessages.aiProactiveCloseDescriptor)
		: formatMessage(sharedMessages.aiProactiveDescriptor, {
			count: unreadCount,
		});

	const badge = !isLoading && (unreadCount ?? 0) > 0 && (
		<Badge max={4} appearance="primary">
			{unreadCount}
		</Badge>
	);

	const loadingIcon = <Loading alt={formatMessage(aiProactiveMessages.loadingIconAltText)} />;
	const suggestedEditIcon = fg('platform-custom-icon-migration') ? <AkSuggestedEditIcon label={title} color="currentColor" /> : <SuggestedEditIcon label={title} />;

	const iconBefore =
		!shouldShowLabel && isLoading ? loadingIcon : suggestedEditIcon;

	const buttonContent = (
		<Inline space="space.075" alignBlock="center">
			<Box xcss={styles.truncatingText}>{formatMessage(sharedMessages.aiProactiveButtonLabel)}</Box>
			{isLoading && loadingIcon}
			{badge}
		</Inline>
	);

	return (
		<LoadingContainer isLoading={isLoading}>
			<ToolbarButton
				onClick={onClick}
				buttonId={TOOLBAR_ACTION_SUBJECT_ID.AI}
				aria-label={title}
				testId="atlassian-intelligence-toolbar-button"
				title={title}
				iconBefore={iconBefore}
				disabled={isDisabled}
				selected={isSelected ?? false}
			>
				{shouldShowLabel && buttonContent}
			</ToolbarButton>
			{!shouldShowLabel && <Box xcss={styles.unreadBadgeCorner}>{badge}</Box>}
		</LoadingContainer>
	);
};

export const PrimaryToolbarButton = ({
	onClick,
	disabled,
	selected,
	loading,
	unreadCount,
	ButtonWrapper,
	api,
}: Props) => {
	const aiButton = (
		<ProactiveAIButton
			onClick={onClick}
			isDisabled={disabled}
			isSelected={selected}
			isLoading={loading}
			unreadCount={unreadCount}
			api={api}
		/>
	);

	const toolbarMutationCount = useEditorToolbarChangeDetector(ButtonWrapper);
	return (
		<span css={[wrapperStyle, svgIconStyles]}>
			{ButtonWrapper ? (
				<ButtonWrapper key={`${toolbarMutationCount}-key-wrapper`}>{aiButton}</ButtonWrapper>
			) : (
				aiButton
			)}
		</span>
	);
};
